import { client } from "./events";
import { ownerHeaders } from "../teams/workspace";

// Event Analytics and Profile › Viewer-hours: the CDN's counts (every hour,
// eventlive-cdk/lambda/viewerHours.ts) and the Mux numbers kept for viewing
// before they started (eventlive-cdk/scripts/muxHistory.ts). The queries read
// the signed-in account's own numbers (eventlive-cdk/appsync/viewer_hours_appsync.py).
//
// Each returns null when there's nothing (yet) or the request fails: the
// screens then leave viewer-hours out rather than show an error.

const countFields = "name devices hours views";
const muxCountFields = "name viewers views hours";

const eventQuery = `
  query viewerHoursEvent($event_id: ID!) {
    viewerHoursEvent(event_id: $event_id) {
      event_id
      counted
      live_hours
      replay_hours
      hours
      views
      peak_devices
      unique_devices
      live_unique_devices
      replay_unique_devices
      fair_use_hours
      over_fair_use
      ticketed
      first_day
      last_day
      updated_at
      days {
        day
        kind
        hours
        devices
        views
        peak_devices
        minute_start
        minute_devices
      }
      log_gaps { day hours }
      countries { ${countFields} }
      regions { ${countFields} }
      device_types { ${countFields} }
      browsers { ${countFields} }
      operating_systems { ${countFields} }
      mux {
        from
        to
        views
        unique_viewers
        watch_hours
        live_hours
        vod_hours
        countries { ${muxCountFields} }
        regions { ${muxCountFields} }
        device_types { ${muxCountFields} }
        browsers { ${muxCountFields} }
        operating_systems { ${muxCountFields} }
      }
    }
  }
`;

const monthFields = `
  month
  live_hours
  replay_hours
  hours
  extra_hours
  events_count
  events_over_fair_use
  updated_at
`;

const monthsQuery = `
  query viewerHoursMonths($limit: Int) {
    viewerHoursMonths(limit: $limit) {
      ${monthFields}
    }
  }
`;

const monthQuery = `
  query viewerHoursMonth($month: String!) {
    viewerHoursMonth(month: $month) {
      ${monthFields}
      events {
        event_id
        slug
        title
        event_datetime_utc
        live_hours
        replay_hours
        hours
        extra_hours
        event_hours
        fair_use_hours
        over_fair_use
        ticketed
      }
    }
  }
`;

const run = async (query, variables, field, headers) => {
  try {
    const { data } = await client.graphql({ query, variables }, headers);
    return data?.[field] ?? null;
  } catch (err) {
    console.warn(`${field} unavailable`, err?.errors?.[0]?.message || err);
    return null;
  }
};

// One event of the account it's in (Teams: the owner's header).
export const fetchEventViewerHours = (event) =>
  event?.id
    ? run(
        eventQuery,
        { event_id: event.id },
        "viewerHoursEvent",
        ownerHeaders(event.broadcaster)
      )
    : Promise.resolve(null);

// The signed-in account's months, newest first.
export const fetchViewerHoursMonths = (limit = 24) =>
  run(monthsQuery, { limit }, "viewerHoursMonths").then((months) =>
    Array.isArray(months) ? months : null
  );

// One month and the events watched in it.
export const fetchViewerHoursMonth = (month) =>
  run(monthQuery, { month }, "viewerHoursMonth");
